import { Badge, IconButton, Text, Tooltip } from "@chakra-ui/react";
import { FunctionComponent, ReactElement, useState, useEffect } from "react";
import { HiTrash, HiPlusCircle } from "react-icons/hi";
import { MdRefresh } from "react-icons/md";

// Components
import DataTable from "../../../shared/DataTable/DataTable";
import CreateReservationModal from "./modals/CreateReservationModal";
import DeleteReservationModal from "./modals/DeleteReservationModal";
import RecoverReservationModal from "./modals/RecoverReservationModal";

// Hooks
import usePermission from "../../../hooks/usePermission";
import { useReservations } from "../../../hooks/useReservations";

// Utils
import { DataTableColumn, KVP, SortDirection } from "../../../utils/types";
import { IParams } from "../../../shared/DataTable/hooks/useSearchParams";
import { reservationManagementRoles, RESERVATION_STATUS, UserRole } from "../../../utils/consts";
import { ListReservationsParams } from "../../../utils/api/reservations";
import { getClaimStatusFromBffStatus } from "../../../utils/helpers";

interface ReservationsTableProps {
	userId?: number; // Optional: filter by specific user (should be Claims Analyst ID)
	userName?: string; // Optional: display name of the user
	triggerAddModal?: boolean; // Optional: trigger to open add modal from parent
	onModalClose?: () => void; // Optional: callback when modal closes
}

/**
 * IMPORTANT: This table should only display reservations for Claims Analysts.
 * The backend ensures reservations can only be created for users with the Claims Analyst role.
 * When used on a user detail page, the parent component should verify the user is a Claims Analyst
 * before rendering this component.
 */

// Table columns definition
const getColumns = (
	hasManagementAccess: boolean,
	handleDelete: (row: KVP) => void,
	handleRecover: (row: KVP) => void
): Array<DataTableColumn> => [
	{
		header: "Status",
		id: "status",
		filterable: false,
		isSortable: true,
		isHidden: false,
		valueMap: (value: string) => {
			const colorScheme = value === RESERVATION_STATUS.ACTIVE ? "green" : "red";
			return (
				<Badge colorScheme={colorScheme} fontSize="xs">
					{value}
				</Badge>
			);
		}
	},
	{
		header: "Claim Status",
		id: "claimStatus",
		filterable: false,
		isSortable: true,
		isHidden: false,
		allData: true,
		valueMap: (row: KVP) => {
			const isDeleted = row.isDeleted === 1;
			const claimStatus = Number(row.claimStatus);

			if (claimStatus === -1 || isNaN(claimStatus)) {
				return (
					<Badge
						colorScheme="blue"
						fontSize="xs"
						opacity={isDeleted ? 0.6 : 1}
						textDecoration={isDeleted ? "line-through" : "none"}>
						All Statuses
					</Badge>
				);
			}
			const statusLabel = getClaimStatusFromBffStatus(claimStatus);
			return (
				<Text opacity={isDeleted ? 0.6 : 1} textDecoration={isDeleted ? "line-through" : "none"}>
					{statusLabel}
				</Text>
			);
		}
	},
	{
		header: "Pass Group ID",
		id: "passGroupId",
		filterable: true,
		isSortable: true,
		isHidden: false,
		allData: true,
		valueMap: (row: KVP) => {
			const isDeleted = row.isDeleted === 1;
			if (row.isAllPassGroups === 1) {
				return (
					<Badge
						colorScheme="blue"
						fontSize="xs"
						opacity={isDeleted ? 0.6 : 1}
						textDecoration={isDeleted ? "line-through" : "none"}>
						All Groups
					</Badge>
				);
			}
			return (
				<Text opacity={isDeleted ? 0.6 : 1} textDecoration={isDeleted ? "line-through" : "none"}>
					{String(row.passGroupId)}
				</Text>
			);
		}
	},
	{
		header: "State",
		id: "state",
		filterable: false,
		isSortable: true,
		isHidden: false,
		allData: true,
		valueMap: (row: KVP) => {
			const isDeleted = row.isDeleted === 1;
			const value = String(row.state || "-");
			return (
				<Text opacity={isDeleted ? 0.6 : 1} textDecoration={isDeleted ? "line-through" : "none"}>
					{value}
				</Text>
			);
		}
	},
	{
		header: "Matter Billing Code",
		id: "benefitCode",
		filterable: false,
		isSortable: true,
		isHidden: false,
		allData: true,
		valueMap: (row: KVP) => {
			const isDeleted = row.isDeleted === 1;
			if (row.isAllBenefits === 1) {
				return (
					<Badge
						colorScheme="blue"
						fontSize="xs"
						opacity={isDeleted ? 0.6 : 1}
						textDecoration={isDeleted ? "line-through" : "none"}>
						All Benefits
					</Badge>
				);
			}
			return (
				<Text opacity={isDeleted ? 0.6 : 1} textDecoration={isDeleted ? "line-through" : "none"}>
					{String(row.benefitCode)}
				</Text>
			);
		}
	},
	{
		header: "Created By",
		id: "createdByName",
		filterable: false,
		isSortable: false,
		isHidden: false,
		allData: true,
		valueMap: (row: KVP) => {
			const isDeleted = row.isDeleted === 1;
			return (
				<Text opacity={isDeleted ? 0.6 : 1} textDecoration={isDeleted ? "line-through" : "none"}>
					{String(row.createdByName)}
				</Text>
			);
		}
	},
	{
		header: "Created On",
		id: "createdOn",
		filterable: false,
		isSortable: true,
		isHidden: false,
		isDateTime: true,
		allData: true,
		valueMap: (row: KVP) => {
			const isDeleted = row.isDeleted === 1;
			return (
				<Text opacity={isDeleted ? 0.6 : 1} textDecoration={isDeleted ? "line-through" : "none"}>
					{String(row.createdOn)}
				</Text>
			);
		}
	},
	{
		header: "",
		id: "actions",
		filterable: false,
		isSortable: false,
		isHidden: !hasManagementAccess,
		allData: true,
		valueMap: (row: KVP) => {
			const isDeleted = row.isDeleted === 1 || row.status === RESERVATION_STATUS.INACTIVE;

			if (isDeleted) {
				return (
					<Tooltip label="Recover">
						<IconButton
							aria-label="Recover reservation"
							icon={<MdRefresh />}
							size="sm"
							variant="ghost"
							colorScheme="blue"
							onClick={() => handleRecover(row)}
						/>
					</Tooltip>
				);
			} else {
				return (
					<Tooltip label="Delete">
						<IconButton
							aria-label="Delete reservation"
							icon={<HiTrash />}
							size="sm"
							variant="ghost"
							colorScheme="red"
							onClick={() => handleDelete(row)}
						/>
					</Tooltip>
				);
			}
		}
	}
];

const DEFAULT_PARAMS: Partial<IParams> = {
	sortDirection: SortDirection.Desc,
	sortField: "createdOn",
	page: 1,
	limit: 20
};

const ReservationsTable: FunctionComponent<ReservationsTableProps> = ({
	userId,
	userName,
	triggerAddModal,
	onModalClose
}) => {
	const { roles } = usePermission();
	const { fetchReservations, reservationsData } = useReservations();

	// Modal states
	const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
	const [isRecoverModalOpen, setIsRecoverModalOpen] = useState<boolean>(false);
	const [selectedReservation, setSelectedReservation] = useState<{
		id: number;
		analystName: string;
		passGroupId: string;
		isAllPassGroups: boolean;
		benefitCode: string;
		isAllBenefits: boolean;
	} | null>(null);

	// Filter states
	const [currentParams, setCurrentParams] = useState<ListReservationsParams>({
		...DEFAULT_PARAMS,
		page: DEFAULT_PARAMS.page || 1,
		limit: DEFAULT_PARAMS.limit || 20,
		includeDeleted: true // Always show deleted items
	});

	// Create empty URLSearchParams for DataTable (we don't use URL params for this table)
	const emptySearchParams = new URLSearchParams();

	// Check if user has management access
	const hasManagementAccess = reservationManagementRoles.some((role) => roles.includes(role as UserRole));

	// Fetch data when params change
	useEffect(() => {
		fetchReservations(currentParams);
	}, [currentParams]); // eslint-disable-line react-hooks/exhaustive-deps

	const handleFilterChange = (params: IParams) => {
		setCurrentParams({
			page: params.page,
			limit: params.limit,
			sortField: params.sortField,
			sortDir: params.sortDirection,
			filterUserId: userId,
			includeDeleted: true
		});
	};

	const refreshData = () => {
		fetchReservations(currentParams);
	};

	const handleDelete = (row: KVP) => {
		setSelectedReservation({
			id: Number(row.id),
			analystName: String(row.analystName),
			passGroupId: String(row.passGroupId),
			isAllPassGroups: row.isAllPassGroups === 1,
			benefitCode: String(row.benefitCode),
			isAllBenefits: row.isAllBenefits === 1
		});
		setIsDeleteModalOpen(true);
	};

	const handleRecover = (row: KVP) => {
		setSelectedReservation({
			id: Number(row.id),
			analystName: String(row.analystName),
			passGroupId: String(row.passGroupId),
			isAllPassGroups: row.isAllPassGroups === 1,
			benefitCode: String(row.benefitCode),
			isAllBenefits: row.isAllBenefits === 1
		});
		setIsRecoverModalOpen(true);
	};

	// Handle external trigger to open modal
	useEffect(() => {
		if (triggerAddModal) {
			setIsCreateModalOpen(true);
			// Notify parent that modal is now open
			if (onModalClose) {
				onModalClose();
			}
		}
	}, [triggerAddModal, onModalClose]);

	const getCustomHeader = (): ReactElement | undefined => {
		// Don't show header button if parent is controlling the modal
		if (triggerAddModal !== undefined) return undefined;
		if (!hasManagementAccess) return undefined;

		return (
			<Tooltip label="Add Reservation">
				<IconButton
					aria-label="Add reservation"
					icon={<HiPlusCircle />}
					size="sm"
					variant="ghost"
					colorScheme="orange"
					onClick={() => setIsCreateModalOpen(true)}
				/>
			</Tooltip>
		);
	};

	return (
		<>
			{/* Modals */}
			<CreateReservationModal
				isOpen={isCreateModalOpen}
				onClose={() => setIsCreateModalOpen(false)}
				onSuccess={() => {
					setIsCreateModalOpen(false);
					refreshData();
				}}
				defaultUserId={userId}
				userName={userName}
			/>

			<DeleteReservationModal
				isOpen={isDeleteModalOpen}
				onClose={() => {
					setIsDeleteModalOpen(false);
					setSelectedReservation(null);
				}}
				onSuccess={() => {
					setIsDeleteModalOpen(false);
					setSelectedReservation(null);
					refreshData();
				}}
				reservation={selectedReservation}
			/>

			<RecoverReservationModal
				isOpen={isRecoverModalOpen}
				onClose={() => {
					setIsRecoverModalOpen(false);
					setSelectedReservation(null);
				}}
				onSuccess={() => {
					setIsRecoverModalOpen(false);
					setSelectedReservation(null);
					refreshData();
				}}
				reservation={selectedReservation}
			/>

			{/* Data Table */}
			<DataTable
				data={reservationsData}
				columns={getColumns(hasManagementAccess, handleDelete, handleRecover)}
				refetchTrigger={(cacheKey: string) => {
					// Parse the cache key to extract params
					const params = new URLSearchParams(cacheKey);
					handleFilterChange({
						page: Number(params.get("page")) || 1,
						limit: Number(params.get("limit")) || 20,
						sortField: params.get("sortField") || undefined,
						sortDirection: params.get("sortDirection") || undefined
					} as IParams);
				}}
				customHeaderActions={getCustomHeader()}
				defaultParams={DEFAULT_PARAMS}
				existingParams={emptySearchParams}
				shouldUpdateUrl={false}
				shouldShowTableContainer={false}
				shouldShowFilterToggle={false}
				shouldShowSelection={false}
			/>
		</>
	);
};

export default ReservationsTable;
